<script setup lang="ts">
import RemoveAttendeeFlowModal from "~/components/Pages/training-plan/modals/RemoveAttendeeFlowModal.vue";
import { useSessionIndividual } from "~/composables/sessions/useSessionIndividual";
import { useCreateProjectStore } from "~/store/project/create-project.store";
import { useProjectsStore } from "~/store/project/projects.store";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { useTrainingsPlansStore } from "~/store/training-plan/trainings-plans.store";
import { TrainingPlanTypesEnum } from "~/utils/enums/training-plan-enums";
import { useSessionDetailsAttendeesStore } from "~/store/sessions/session-details-attendees-store";
import type { RemoveProjectUsersDtoRequestActionEnum } from "@simbelapp/order-sdk";

const sessionIndividual = useSessionIndividual();
const sessionStore = useSessionsStore();
const projectStore = useProjectsStore();
const sessionDetailsStore = useSessionDetailsStore();
const { sessionDetails } = storeToRefs(sessionDetailsStore);
const createProjectStore = useCreateProjectStore();
const sessionAttendeesDetailsStore = useSessionDetailsAttendeesStore();
const trainingPlanStore = useTrainingsPlansStore();
const userInfos = computed(() => sessionIndividual.getUserInfos());

// For single user removal, count is 1 if linked to a request
const linkedAttendeesCount = computed(() => (sessionDetails.value?.isLinkedToARequest ? 1 : 0));

const emit = defineEmits(["hide"]);

// State to store the request action
const requestAction = ref<RemoveProjectUsersDtoRequestActionEnum | null>(null);
const requestComment = ref<string | null>(null);

const deleteConsequences = [
  'Si l\'inscription était liée à une demande, celle-ci ré-apparaîtra dans la section "À traiter".',
  "Les éventuels documents associés seront supprimés",
];

function hideModal() {
  requestAction.value = null;
  requestComment.value = null;
  emit("hide");
}

async function onConfirmDelete() {
  await removeProjectUser();
}

async function onConfirmRequest(action: RemoveProjectUsersDtoRequestActionEnum, comment?: string) {
  requestAction.value = action;
  requestComment.value = comment || null;
  await removeProjectUser();
}

async function removeProjectUser() {
  if (sessionIndividual.sourceId.value && userInfos.value.userId) {
    await projectStore.removeProjectUsers(
      sessionIndividual.sourceId.value,
      [userInfos.value.userId],
      requestAction.value,
      requestComment.value,
    );

    if (!sessionDetailsStore.sessionViewModal) {
      await trainingPlanStore.fetchTrainingsPlansIndividual({ itemType: TrainingPlanTypesEnum.PROJECTS });
    } else if (sessionDetails.value?.sessionId && sessionDetails.value?.itemType) {
      await sessionDetailsStore.fetchSessionDetails(sessionDetails.value?.sessionId, sessionDetails.value?.itemType);
      await sessionAttendeesDetailsStore.fetchSessionAttendeesFullDatas(
        sessionDetails.value?.sessionId,
        sessionDetails.value?.itemType,
      );

      createProjectStore.removeFinancingUserBucket(userInfos.value.userId);
    }
    sessionStore.showSessionModal = false;
    hideModal();
  }
}
</script>

<template>
  <RemoveAttendeeFlowModal
    :has-linked-request="sessionDetails?.isLinkedToARequest || false"
    :linked-attendees-count="linkedAttendeesCount"
    :show-notify-step="false"
    :delete-consequences="deleteConsequences"
    @hide="hideModal"
    @confirm-delete="onConfirmDelete"
    @confirm-request="onConfirmRequest"
  />
</template>

<style scoped lang="scss"></style>
